
import { MarketplaceStrategy } from "@/types/strategy-types";
import { useEffect, useState } from "react";
import EmptyState from "./my-strategies/EmptyState";
import LoadingState from "./my-strategies/LoadingState";
import UnfollowDialog from "./my-strategies/UnfollowDialog";
import StrategyCard from "@/components/marketplace/StrategyCard";
import { formatDate, getExchangeLogo } from "@/components/marketplace/utils/marketplace-utils";
import { useAccountPerformance } from "@/hooks/marketplace/useAccountPerformance";

interface MyStrategiesProps {
  strategies: MarketplaceStrategy[];
  onSelectStrategy: (name: string) => void;
  onUnfollow?: (id: string) => void;
  isLoading?: boolean;
}

const MyStrategies = ({ strategies, onSelectStrategy, onUnfollow, isLoading = false }: MyStrategiesProps) => {
  const [unfollowDialogOpen, setUnfollowDialogOpen] = useState(false);
  const [selectedStrategyId, setSelectedStrategyId] = useState<string | null>(null);


    // Use our custom hook to get account performance data
    const { accountPerformance, performanceLoading } = useAccountPerformance(strategies, false);

  const handleUnfollowClick = (id: string) => {
    setSelectedStrategyId(id);
    setUnfollowDialogOpen(true);
  };

  const confirmUnfollow = () => {
    if (selectedStrategyId && onUnfollow) {
      onUnfollow(selectedStrategyId);
    }
    setUnfollowDialogOpen(false);
  };

  // Define a function to get risk label for the strategy card
  const getRiskLabel = (level: number) => {
    switch (level) {
      case 1:
        return { text: "Low", color: "bg-green-500" };
      case 2:
        return { text: "Medium", color: "bg-yellow-500" };
      case 3:
        return { text: "High", color: "bg-red-500" };
      default:
        return { text: "Unknown", color: "bg-gray-500" };
    }
  };

  return (
    <section className="mb-12">
      <h2 className="text-2xl font-bold mb-6">My Strategies</h2>
      
      {isLoading ? (
        <LoadingState />
      ) : strategies.length > 0 && accountPerformance !== undefined ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {strategies.map((strategy) => (
            <div key={strategy.id} className="relative">
              <StrategyCard
                strategy={strategy}
                onSelect={onSelectStrategy}
                getRiskLabel={getRiskLabel}
                formatDate={formatDate}
                getExchangeLogo={getExchangeLogo}
                unfollowAction={() => handleUnfollowClick(strategy.id)}
                performanceData={accountPerformance?.[strategy.strategy_id]}
                showUnfollowButton={true}
                followLabel="View Details"
              />
            </div>
          ))}
        </div>
      ) : (
        <EmptyState />
      )}

      <UnfollowDialog
        open={unfollowDialogOpen}
        onOpenChange={setUnfollowDialogOpen}
        onConfirm={confirmUnfollow}
      />
    </section>
  );
};

export default MyStrategies;
